import {
  faFileExport,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {
  useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  ButtonSize, ButtonStyle,
  IconButtonStyle,
} from '~/_shared/baseComponents/buttons';
import { LottieAnimations } from '~/_shared/baseComponents/lottieAnimation';
import { LottieAnimationTypes } from '~/_shared/baseComponents/lottieAnimation/lottieAnimation.types';
import { AnimationPreloadComponent } from '~/_shared/components/topUnderbar/statusItem/animationPreload.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { ProximityType } from '~/_shared/types/proximity/proximity.enums';
import { isIndividualRadius } from '~/_shared/types/proximity/proximity.types';
import { copyToClipboard } from '~/_shared/utils/clipboard/clipboard.helpers';
import type { Uuid } from '~/_shared/utils/createUuid';
import { downloadFile } from '~/_shared/utils/document/document.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import { useProximities } from '~/proximity/useProximities';
import { proximityExportRemove } from '~/store/frontendState/proximityTool/proximityTool.actionCreators';
import { useProximityToolExportsSelector } from '~/store/frontendState/proximityTool/proximityTool.selectors';
import {
  type ExportProximityResult, ExportProximityResultType,
} from '~/store/frontendState/proximityTool/proximityTool.state';
import { type TopUnderbarItem } from '../item/topUnderbarItem.types';
import {
  type TopUnderbarSavingStatusComponentProps, TopUnderbarStatusItemComponent,
} from './topUnderbarStatus.component';

export const proximityExportTimeRange = { fromMinutes: 5, toMinutes: 10 } as const;

const doneAnimationType = LottieAnimationTypes.CircleDownload;
const doneAnimationProps = {
  segment: LottieAnimations[doneAnimationType].segments.success,
  speed: 1,
  size: 30,
  autoplay: true,
} as const;

const exportingAnimationType = LottieAnimationTypes.CircleArrowDown;
const exportingAnimationProps = {
  segment: LottieAnimations[exportingAnimationType].segments.default,
  size: 30,
  autoplay: true,
  loop: true,
} as const;

const errorAnimationType = LottieAnimationTypes.Failed;
const errorAnimationProps = {
  segment: LottieAnimations.Failed.segments.default,
  size: 30,
  autoplay: true,
} as const;

const animationsToPreload = [
  {
    type: doneAnimationType,
    ...doneAnimationProps,
  }, {
    type: exportingAnimationType,
    ...exportingAnimationProps,
  }, {
    type: errorAnimationType,
    ...errorAnimationProps,
  },
];

const animationPreload = (
  <AnimationPreloadComponent animationsToPreload={animationsToPreload} />
);

type ProximityExportInfo = Readonly<{
  id: Uuid;
  request: Readonly<{
    proximityIds?: ReadonlyArray<Uuid>;
    exportAsText: boolean;
  }>;
  resultPages: ReadonlyArray<ExportProximityResult>;
}>;

const sampleProximityExportData: ProximityExportInfo[] = [{
  id: 'sample-proximity-id-1',
  request: {
    proximityIds: ['proximity-1', 'proximity-2', 'proximity-3'],
    exportAsText: false,
  },
  resultPages: [{
    type: ExportProximityResultType.Pending,
    data: null,
    error: null,
  }],
}, {
  id: 'sample-proximity-id-2',
  request: {
    proximityIds: ['proximity-1'],
    exportAsText: false,
  },
  resultPages: [{
    type: ExportProximityResultType.Success,
    data: {
      nextPageId: undefined,
      file: 'https://example.com/export/file.csv',
      data: undefined,
    },
    error: null,
  }],
},
{
  id: 'sample-proximity-id-2',
  request: {
    proximityIds: ['proximity-1'],
    exportAsText: false,
  },
  resultPages: [{
    type: ExportProximityResultType.Error,
    data: null,
    error: 'An error occurred during export.',
  }],
}];

export const useProximityExportStatusItems = ({
  generateSamples,
}: {
  generateSamples?: boolean;
} = {}): ReadonlyArray<TopUnderbarItem> => {
  const [t] = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const proximityExportState = useProximityToolExportsSelector();
  const proximities = useProximities();
  const [copiedItemIds, setCopiedItemIds] = useState<Set<Uuid>>(new Set());

  return useMemo(() => {
    const items: TopUnderbarItem[] = (generateSamples ? sampleProximityExportData : proximityExportState).map((item) => {
      const proximityExportId = item.id;
      const isDone = item.resultPages.every((page) => page.type === ExportProximityResultType.Success);
      const incompletePageIndex = item.resultPages.findIndex((page) => page.type === ExportProximityResultType.Pending);
      const error = item.resultPages.find((page) => page.type === ExportProximityResultType.Error)?.error;

      let numberOfIndividualProximities = 0;
      let numberOfGroupProximities = 0;
      let numberOfDriveTimePolygons = 0;

      item.request.proximityIds?.forEach((proximityId) => {
        const proximity = proximities.find((p) => p.id === proximityId);
        if (proximity && proximity.type === ProximityType.DistanceRadius) {
          if (isIndividualRadius(proximity)) {
            numberOfIndividualProximities += 1;
          }
          else {
            numberOfGroupProximities += 1;
          }
        }
        else if (proximity && proximity.type === ProximityType.DriveTimePolygon) {
          numberOfDriveTimePolygons += 1;
        }
      });

      let animation: TopUnderbarSavingStatusComponentProps['animation'] = {
        type: exportingAnimationType,
        ...exportingAnimationProps,
      };
      if (isDone) {
        animation = {
          type: doneAnimationType,
          ...doneAnimationProps,
        };
      }
      else if (error) {
        animation = {
          type: errorAnimationType,
          ...errorAnimationProps,
        };
      }

      const isCopied = copiedItemIds.has(proximityExportId);

      const copyToClipboardButtonProps = item.request.exportAsText && isDone && item.resultPages[0]?.data.data
        ? {
          buttonStyle: ButtonStyle.Primary,
          onClick: () => {
            copyToClipboard(item.resultPages[0]?.data?.data ?? '');
            setCopiedItemIds(prev => new Set(prev).add(proximityExportId));
            setTimeout(() => {
              setCopiedItemIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(proximityExportId);
                return newSet;
              });
            }, 3000);
          },
          text: t('Copy to Clipboard'),
          tooltip: isCopied ? t('Copied to Clipboard!') : undefined,
        }
        : undefined;

      let button = undefined;
      if (!isDone && !error) {
        button = {
          buttonStyle: ButtonStyle.Danger,
          size: ButtonSize.Small,
          text: t('Cancel'),
          onClick: () => {
            dispatch(proximityExportRemove(proximityExportId));
          },
        };
      }
      else if (isDone && !error && !item.request.exportAsText) {
        button = {
          buttonStyle: ButtonStyle.Primary,
          size: ButtonSize.Small,
          text: t('Download again'),
          onClick: () => {
            const downloadLinks = item.resultPages.map((page) => {
              if (page.type === ExportProximityResultType.Success && page.data.file) {
                return page.data.file;
              }
              return null;
            }).filter(notNullOrUndefined);
            if (downloadLinks.length) {
              downloadLinks.forEach((link, index) => {
                setTimeout(() => {
                  downloadFile(link);
                }, index * 2000);
              });
            }
          },
        };
      }
      else if (copyToClipboardButtonProps) {
        button = copyToClipboardButtonProps;
      }

      let suffixText = undefined;
      if (error) {
        suffixText = {
          value: error,
          color: theme.textColors.danger,
        };
      }
      else if (!item.request.exportAsText && incompletePageIndex > 0 && !isDone) {
        suffixText = {
          value: `${t('Exporting Page {n}', { n: incompletePageIndex + 1 })}. ${'The download should start automatically.'}`,
          color: theme.textColors.warning,
        };
      }
      else if (!isDone) {
        suffixText = {
          value: t('This process can take up to {{fromMinutes}}-{{toMinutes}} minutes.', proximityExportTimeRange),
          color: theme.textColors.warning,
        };
      }
      else if (!item.request.exportAsText && isDone) {
        suffixText = {
          value: `${t('Export Complete')}. ${t('The download should start automatically.')}`,
          color: theme.textColors.warning,
        };
      }

      return {
        key: `proximity-export-${item.id}`,
        sticky: true,
        icon: faFileExport,
        colors: {
          color: theme.contrasts.selectedAccent2,
          backgroundColor: theme.contrasts.selectedAccent2Background,
        },
        children: (
          <>
            {animationPreload}
            <TopUnderbarStatusItemComponent
              animation={animation}
              text={{
                value: `${t('Exporting')}`,
                color: theme.textColors.light,
              }}
              subtexts={[
                ...(numberOfIndividualProximities > 0 ? [{
                  value: `• ${t('Distance Radius{{type}}{{count}}', {
                    count: numberOfIndividualProximities, type: t('Individual Location'),
                  })}`,
                }] : []),
                ...(numberOfGroupProximities > 0 ? [{
                  value: `• ${t('Distance Radius{{type}}{{count}}', {
                    count: numberOfGroupProximities, type: t('Group'),
                  })}`,
                }] : []),
                ...(numberOfDriveTimePolygons > 0 ? [{
                  value: `• ${t('Drive Time Polygons{{count}}', {
                    count: numberOfDriveTimePolygons,
                  })}`,
                }] : []),
              ]}
              suffixText={suffixText}
              button={button}
              iconButton={(isDone || error)
                ? {
                  icon: faXmark,
                  onClick: () => {
                    dispatch(proximityExportRemove(proximityExportId));
                  },
                  buttonStyle: IconButtonStyle.Secondary,
                  size: 's',
                } : undefined
              }
            />
          </>
        ),
      };
    });

    return items;
  }, [copiedItemIds, dispatch, generateSamples, proximities, proximityExportState, t, theme.contrasts.selectedAccent2, theme.contrasts.selectedAccent2Background, theme.textColors.danger, theme.textColors.light, theme.textColors.warning]);
};
